import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, CalendarDays, Check, ChevronLeft, ChevronRight, Download, Landmark, Plus, Sparkles, Sun, Trash2 } from "lucide-react";
import { toast } from "react-hot-toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  deleteOfficeHolidayApi,
  getOfficeHolidaysApi,
  saveOfficeHolidayApi,
} from "../../app/api";
import { fetchIndianPublicHolidays, normalizeOfficeHoliday } from "../../utils/publicHolidays";

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 6 }, (_, index) => currentYear + index);
const monthOptions = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const toDateKey = (date) => String(date || "").slice(0, 10);

const parseDateKey = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const formatDateKey = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDay = (date) =>
  new Date(`${toDateKey(date)}T00:00:00`).toLocaleDateString("en-IN", {
    weekday: "long",
  });

const formatMonth = (date) =>
  new Date(`${toDateKey(date)}T00:00:00`).toLocaleDateString("en-IN", {
    month: "long",
  });

const formatDateNumber = (date) =>
  new Date(`${toDateKey(date)}T00:00:00`).toLocaleDateString("en-IN", {
    day: "2-digit",
  });

const isWeekend = (date) => {
  const day = new Date(`${toDateKey(date)}T00:00:00`).getDay();
  return day === 0 || day === 6;
};

export default function Holidays() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const canManage = [1, 2, 3].includes(Number(user?.role_id));
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [officeHolidays, setOfficeHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [holidayMonthMenuOpen, setHolidayMonthMenuOpen] = useState(false);
  const [form, setForm] = useState({
    holiday_date: `${selectedYear}-01-01`,
    title: "",
    description: "",
  });

  const loadHolidays = useCallback(async () => {
    setLoading(true);
    const [publicList, officeRes] = await Promise.all([
      fetchIndianPublicHolidays(selectedYear),
      getOfficeHolidaysApi(selectedYear),
    ]);

    setPublicHolidays(publicList || []);
    setOfficeHolidays(officeRes?.success ? (officeRes.data || []).map(normalizeOfficeHoliday) : []);
    setLoading(false);
  }, [selectedYear]);

  useEffect(() => {
    loadHolidays();
    setForm((current) => ({
      ...current,
      holiday_date: `${selectedYear}-01-01`,
    }));
  }, [loadHolidays, selectedYear]);

  const grouped = useMemo(() => {
    const merged = [
      ...officeHolidays.map((holiday) => ({ ...holiday, sourceType: "office" })),
      ...publicHolidays.map((holiday) => ({ ...holiday, sourceType: "public" })),
    ].sort((a, b) => new Date(a.event_date) - new Date(b.event_date));

    return merged.reduce((acc, holiday) => {
      const month = formatMonth(holiday.event_date);
      acc[month] = acc[month] || [];
      acc[month].push(holiday);
      return acc;
    }, {});
  }, [officeHolidays, publicHolidays]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.title.trim() || !form.holiday_date) {
      toast.error("Holiday date and title are required");
      return;
    }

    setSaving(true);
    const res = await saveOfficeHolidayApi({
      holiday_date: form.holiday_date,
      title: form.title.trim(),
      description: form.description.trim(),
    });
    setSaving(false);

    if (res?.success) {
      toast.success("Office holiday saved");
      setForm((current) => ({ ...current, title: "", description: "" }));
      loadHolidays();
    } else {
      toast.error(res?.message || "Could not save office holiday");
    }
  };

  const handleDelete = async (holiday) => {
    const rawId = String(holiday.id || "").replace("office-holiday-", "");
    if (!rawId) return;

    const res = await deleteOfficeHolidayApi(rawId);
    if (res?.success) {
      toast.success("Office holiday deleted");
      loadHolidays();
    } else {
      toast.error(res?.message || "Could not delete office holiday");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 md:mt-20 md:mb-20">
      <style>
        {`
          .uptask-holiday-datepicker-popper {
            z-index: 99999 !important;
          }
          .uptask-holiday-datepicker {
            width: min(92vw, 360px) !important;
            overflow: hidden !important;
            border: 1px solid rgba(253,186,116,0.32) !important;
            border-radius: 24px !important;
            background: #1b153c !important;
            color: #fff !important;
            font-family: inherit !important;
            box-shadow: 0 34px 90px rgba(5,2,18,0.72), inset 0 1px 0 rgba(255,255,255,0.12) !important;
          }
          .uptask-holiday-datepicker,
          .uptask-holiday-datepicker *,
          .uptask-holiday-datepicker .react-datepicker__month-container,
          .uptask-holiday-datepicker .react-datepicker__month,
          .uptask-holiday-datepicker .react-datepicker__week {
            opacity: 1 !important;
          }
          .uptask-holiday-datepicker .react-datepicker__triangle {
            display: none !important;
          }
          .uptask-holiday-datepicker .react-datepicker__month-container,
          .uptask-holiday-datepicker .react-datepicker__month {
            width: 100% !important;
            background: #1b153c !important;
          }
          .uptask-holiday-datepicker .react-datepicker__header {
            padding: 0 !important;
            border-bottom: 1px solid rgba(255,255,255,0.10) !important;
            background: #21184a !important;
          }
          .uptask-holiday-datepicker .react-datepicker__current-month,
          .uptask-holiday-datepicker .react-datepicker-time__header,
          .uptask-holiday-datepicker .react-datepicker-year-header {
            color: #fff !important;
            margin-bottom: 14px !important;
            font-weight: 900 !important;
            letter-spacing: 0.02em !important;
          }
          .uptask-holiday-datepicker .react-datepicker__month {
            margin: 0 !important;
            padding: 10px 8px 14px !important;
          }
          .uptask-holiday-datepicker .react-datepicker__day-name,
          .uptask-holiday-datepicker .react-datepicker__day {
            width: 2.25rem !important;
            line-height: 2.25rem !important;
            margin: 0.08rem !important;
            border-radius: 13px !important;
            color: rgba(255,255,255,0.84) !important;
            font-weight: 800 !important;
          }
          .uptask-holiday-datepicker .react-datepicker__day-name {
            color: rgba(254,215,170,0.78) !important;
            font-size: 0.74rem !important;
            letter-spacing: 0.08em !important;
          }
          .uptask-holiday-datepicker .react-datepicker__day:hover {
            background: rgba(251,146,60,0.18) !important;
            color: #fff !important;
          }
          .uptask-holiday-datepicker .react-datepicker__day--outside-month,
          .uptask-holiday-datepicker .react-datepicker__day--disabled {
            color: rgba(255,255,255,0.24) !important;
            background: transparent !important;
          }
          .uptask-holiday-datepicker .react-datepicker__day--today {
            outline: 1px solid rgba(251,191,36,0.62) !important;
          }
          .uptask-holiday-datepicker .react-datepicker__day--selected,
          .uptask-holiday-datepicker .react-datepicker__day--keyboard-selected {
            background: linear-gradient(135deg, #fb923c, #ef4444) !important;
            color: #fff !important;
            box-shadow: 0 12px 26px rgba(251,146,60,0.30) !important;
          }
          .uptask-holiday-datepicker .react-datepicker__navigation-icon::before {
            border-color: rgba(255,255,255,0.9) !important;
          }
          @media (max-width: 640px) {
            .uptask-holiday-datepicker-popper {
              left: 50% !important;
              right: auto !important;
              transform: translate3d(-50%, 0, 0) !important;
              width: min(92vw, 352px) !important;
            }
            .uptask-holiday-datepicker .react-datepicker__day-name,
            .uptask-holiday-datepicker .react-datepicker__day {
              width: calc((92vw - 54px) / 7) !important;
              max-width: 2.25rem !important;
              line-height: 2.05rem !important;
              margin: 0.04rem !important;
              font-size: 0.86rem !important;
            }
          }
        `}
      </style>
      <div className="relative overflow-hidden rounded-[30px] border border-white/10 bg-gradient-to-br from-[#1e174a] via-[#171035] to-[#0f0c29] p-4 text-white shadow-[0_40px_100px_-20px_rgba(0,0,0,0.7)] sm:p-6 md:p-10">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_8%,rgba(34,211,238,0.12),transparent_34%),radial-gradient(circle_at_88%_0%,rgba(249,115,22,0.12),transparent_34%)]" />

        <div className="relative mb-8 flex flex-col justify-between gap-5 sm:flex-row sm:items-center">
          <div className="flex items-center gap-4">
            <div className="rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 p-3 shadow-lg shadow-orange-900/25">
              <Sun className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="flex items-center gap-3 text-xl font-semibold">
                Holiday Calendar
                <span className="rounded-full bg-white/10 px-3 py-1 text-[10px] font-black uppercase tracking-[2px] text-orange-200">
                  {selectedYear}
                </span>
              </h1>
              <p className="text-sm text-white/50">
                Public festivals plus office holidays
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <select
              value={selectedYear}
              onChange={(event) => setSelectedYear(Number(event.target.value))}
              className="rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white outline-none"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year} className="bg-[#171035]">
                  {year}
                </option>
              ))}
            </select>

            <a
              href="/Holidays 2026 1.pdf"
              download
              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-bold hover:bg-white/10"
            >
              <Download className="h-4 w-4 text-blue-300" />
              PDF
            </a>

            <button
              onClick={() => navigate("/")}
              className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-2 text-xs font-bold text-red-200 hover:bg-red-500 hover:text-white"
            >
              Close
            </button>
          </div>
        </div>

        {canManage && (
          <form
            onSubmit={handleSubmit}
            className="relative z-[80] mb-10 grid gap-3 rounded-[26px] border border-cyan-200/15 bg-white/[0.06] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_20px_55px_rgba(4,2,22,0.22)] backdrop-blur-xl md:grid-cols-[220px_1fr_1.4fr_auto]"
          >
            <div className="relative">
              <CalendarDays className="pointer-events-none absolute left-4 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-orange-100/70" />
              <DatePicker
                selected={parseDateKey(form.holiday_date)}
                onChange={(date) => setForm((current) => ({ ...current, holiday_date: formatDateKey(date) }))}
                minDate={new Date(selectedYear, 0, 1)}
                maxDate={new Date(selectedYear, 11, 31)}
                dateFormat="dd/MM/yyyy"
                placeholderText="Holiday date"
                calendarClassName="uptask-holiday-datepicker"
                popperClassName="uptask-holiday-datepicker-popper"
                wrapperClassName="w-full"
                popperPlacement="bottom-start"
                withPortal={window.innerWidth < 640}
                onCalendarClose={() => setHolidayMonthMenuOpen(false)}
                renderCustomHeader={({
                  date,
                  decreaseMonth,
                  increaseMonth,
                  changeMonth,
                  prevMonthButtonDisabled,
                  nextMonthButtonDisabled,
                }) => (
                  <div className="bg-[#21184a] px-4 py-4">
                    <div className="mb-4 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                        className="grid h-9 w-9 place-items-center rounded-full bg-white/[0.08] text-white transition hover:bg-white/[0.14] disabled:cursor-not-allowed disabled:opacity-30"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <div className="text-center">
                        <div className="text-[10px] font-black uppercase tracking-[0.28em] text-orange-100/55">
                          Office Holiday
                        </div>
                        <div className="mt-1 text-lg font-black uppercase tracking-[0.06em] text-white">
                          {monthOptions[date.getMonth()]} {selectedYear}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                        className="grid h-9 w-9 place-items-center rounded-full bg-white/[0.08] text-white transition hover:bg-white/[0.14] disabled:cursor-not-allowed disabled:opacity-30"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-[1fr_auto] gap-3">
                      <div className="relative">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setHolidayMonthMenuOpen((open) => !open);
                          }}
                          className="flex h-10 w-full items-center justify-between rounded-2xl border border-white/14 bg-[#322657] px-4 text-sm font-black text-white outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition hover:bg-[#3b2d66]"
                        >
                          {monthOptions[date.getMonth()]}
                          <ChevronRight className={`h-4 w-4 transition ${holidayMonthMenuOpen ? "rotate-90 text-orange-200" : "text-white/70"}`} />
                        </button>
                        {holidayMonthMenuOpen && (
                          <div
                            className="absolute left-0 right-0 top-[calc(100%+8px)] z-[100000] max-h-64 overflow-y-auto rounded-2xl border border-white/14 bg-[#160f33] p-1.5 shadow-[0_24px_70px_rgba(4,2,18,0.75),inset_0_1px_0_rgba(255,255,255,0.10)]"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {monthOptions.map((month, index) => {
                              const active = date.getMonth() === index;

                              return (
                                <button
                                  key={month}
                                  type="button"
                                  onClick={() => {
                                    changeMonth(index);
                                    setHolidayMonthMenuOpen(false);
                                  }}
                                  className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-black transition ${
                                    active
                                      ? "bg-orange-400 text-[#170f2d]"
                                      : "text-white/82 hover:bg-white/[0.08] hover:text-white"
                                  }`}
                                >
                                  {month}
                                  {active && <Check className="h-4 w-4" />}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <span className="grid h-10 min-w-20 place-items-center rounded-2xl border border-white/14 bg-[#322657] px-4 text-sm font-black text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
                        {selectedYear}
                      </span>
                    </div>
                  </div>
                )}
                className="w-full rounded-2xl border border-white/15 bg-white/[0.08] px-11 py-3 text-sm font-semibold text-white outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_12px_30px_rgba(0,0,0,0.16)] transition placeholder:text-white/35 focus:border-orange-200/50 focus:bg-white/[0.11] focus:ring-2 focus:ring-orange-300/15"
              />
            </div>
            <input
              value={form.title}
              onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
              placeholder="Office holiday title"
              className="rounded-2xl border border-white/15 bg-white/[0.08] px-4 py-3 text-sm font-semibold text-white outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition placeholder:text-white/35 focus:border-orange-200/50 focus:bg-white/[0.11] focus:ring-2 focus:ring-orange-300/15"
            />
            <input
              value={form.description}
              onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              placeholder="Optional note"
              className="rounded-2xl border border-white/15 bg-white/[0.08] px-4 py-3 text-sm font-semibold text-white outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition placeholder:text-white/35 focus:border-orange-200/50 focus:bg-white/[0.11] focus:ring-2 focus:ring-orange-300/15"
            />
            <button
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-400 px-5 py-3 text-sm font-black text-[#061824] shadow-lg shadow-cyan-900/20 hover:bg-cyan-300 disabled:opacity-60"
            >
              <Plus className="h-4 w-4" />
              {saving ? "Saving" : "Add"}
            </button>
          </form>
        )}

        {loading ? (
          <div className="relative py-20 text-center text-sm text-white/55">Loading holiday calendar...</div>
        ) : (
          <div className="relative z-0 space-y-10 md:space-y-14">
            {Object.entries(grouped).map(([month, list]) => (
              <div key={month}>
                <div className="mb-5 flex items-center gap-4">
                  <h2 className="text-xl font-black uppercase tracking-[4px] text-white/40">
                    {month}
                  </h2>
                  <div className="h-px flex-1 bg-white/8" />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {list.map((holiday) => {
                    const office = holiday.sourceType === "office";
                    const weekend = isWeekend(holiday.event_date);

                    return (
                      <div
                        key={holiday.id}
                        className={`group relative overflow-hidden rounded-[24px] border p-5 transition hover:-translate-y-1 ${
                          office
                            ? "border-emerald-300/25 bg-emerald-400/10"
                            : weekend
                              ? "border-red-300/25 bg-red-400/10"
                              : "border-white/10 bg-white/[0.055] hover:bg-white/[0.08]"
                        }`}
                      >
                        <div className="flex justify-between gap-4">
                          <div className="flex min-w-0 gap-4">
                            <div className={`flex h-24 w-20 shrink-0 flex-col overflow-hidden rounded-2xl ${office ? "bg-emerald-400/18" : "bg-white/10"}`}>
                              <div className="grid h-8 place-items-center bg-white/10 text-[10px] font-black uppercase tracking-widest">
                                {month.slice(0, 3)}
                              </div>
                              <div className="flex flex-1 flex-col items-center justify-center">
                                <span className="text-2xl font-black">{formatDateNumber(holiday.event_date)}</span>
                                <span className="text-[10px] font-bold uppercase text-white/55">{formatDay(holiday.event_date).slice(0, 3)}</span>
                              </div>
                            </div>

                            <div className="min-w-0">
                              <div className="mb-2 flex flex-wrap items-center gap-2">
                                <h3 className="truncate text-lg font-black">{holiday.title || holiday.name}</h3>
                                <span className={`rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-wider ${office ? "bg-emerald-300/15 text-emerald-100" : "bg-amber-300/15 text-amber-100"}`}>
                                  {office ? "Office Holiday" : "Festival"}
                                </span>
                              </div>
                              <p className="text-sm leading-relaxed text-white/50">
                                {holiday.meta?.description || holiday.meta?.source || (weekend ? "Falls on weekend" : "Public holiday")}
                              </p>
                            </div>
                          </div>

                          <div className="flex shrink-0 flex-col items-end gap-3">
                            <div className={`rounded-xl p-3 ${office ? "bg-emerald-300/15 text-emerald-100" : "bg-amber-300/15 text-amber-100"}`}>
                              {office ? <Building2 className="h-5 w-5" /> : <Landmark className="h-5 w-5" />}
                            </div>
                            {canManage && office && (
                              <button
                                onClick={() => handleDelete(holiday)}
                                className="rounded-lg bg-red-500/10 p-2 text-red-200 opacity-0 transition hover:bg-red-500/20 group-hover:opacity-100"
                                title="Delete office holiday"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {Object.keys(grouped).length === 0 && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-10 text-center text-white/55">
                No holidays found for {selectedYear}
              </div>
            )}
          </div>
        )}

        <div className="relative mt-14 flex flex-col items-center border-t border-white/5 pt-8 text-center text-white/40">
          <Sparkles className="mb-3 h-7 w-7 text-blue-300" />
          <p className="text-xs font-black uppercase tracking-[4px]">
            Synced public calendar with office holidays
          </p>
        </div>
      </div>
    </div>
  );
}
